// =============================================================
// VARIANCE — The Executive Command Center
// File      : Vendors.tsx
// Location  : /home/Applications/variance/web/src/pages/Vendors.tsx
// Owner     : VARIANCE frontend application only
// Purpose   : Displays the Vendors Master and provides a form
//             to register Consultants, Contractors, and Suppliers.
// =============================================================

import { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';

interface Company { id: string; name: string; }
interface Vendor {
  id: string;
  name: string;
  vendor_type: string;
  goods_services: string | null;
  phone1: string | null;
  representative: string | null;
}

export function Vendors() {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [formData, setFormData] = useState({
    company_id: '',
    name: '',
    vendor_type: 'Contractor',
    custom_type: '',
    goods_services: '',
    address: '',
    email: '',
    phone1: '',
    phone2: '',
    representative: '',
    designation: ''
  });
  const [formError, setFormError] = useState('');

  const fetchVendors = () => {
    setIsLoading(true);
    fetch('/api/vendors')
      .then(res => res.json())
      .then(data => { if (!data.error) setVendors(data); setIsLoading(false); })
      .catch(() => setIsLoading(false));
  };

  const fetchCompanies = () => {
    fetch('/api/companies')
      .then(res => res.json())
      .then(data => {
        if (!data.error && data.length > 0) {
          setCompanies(data);
          setFormData(prev => ({ ...prev, company_id: data[0].id }));
        }
      });
  };

  useEffect(() => {
    fetchVendors();
    fetchCompanies();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    try {
      const payload = {
        ...formData,
        vendor_type: formData.vendor_type === 'other' ? formData.custom_type : formData.vendor_type
      };
      delete (payload as any).custom_type;

      const res = await fetch('/api/vendors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create vendor');
      
      setIsModalOpen(false);
      setFormData({ company_id: companies[0]?.id || '', name: '', vendor_type: 'Contractor', custom_type: '', goods_services: '', address: '', email: '', phone1: '', phone2: '', representative: '', designation: '' });
      fetchVendors();
    } catch (err: any) {
      setFormError(err.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Vendors Master</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Manage Consultants, Contractors, and Suppliers.</p>
        </div>
        <button onClick={() => setIsModalOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white hover:opacity-90" style={{ backgroundColor: 'var(--brand-accent)' }}>
          <Plus size={18} /> Add Vendor
        </button>
      </div>

      <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
        <table className="w-full text-left border-collapse">
          <thead>
            <tr style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
              <th className="p-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Vendor Name</th>
              <th className="p-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Type</th>
              <th className="p-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Goods / Services</th>
              <th className="p-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Representative</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={4} className="p-8 text-center text-gray-400">Loading...</td></tr>
            ) : vendors.length === 0 ? (
              <tr><td colSpan={4} className="p-8 text-center text-gray-400">No vendors registered yet.</td></tr>
            ) : (
              vendors.map(v => (
                <tr key={v.id} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                  <td className="p-4 font-medium" style={{ color: 'var(--text-primary)' }}>{v.name}</td>
                  <td className="p-4 text-sm" style={{ color: 'var(--brand-accent)' }}>{v.vendor_type}</td>
                  <td className="p-4 text-sm" style={{ color: 'var(--text-secondary)' }}>{v.goods_services || '-'}</td>
                  <td className="p-4 text-sm" style={{ color: 'var(--text-secondary)' }}>{v.representative ? `${v.representative} (${v.phone1})` : '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm overflow-y-auto py-10">
          <div className="w-full max-w-2xl p-6 rounded-xl shadow-2xl" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
            <h2 className="text-xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>Add New Vendor</h2>
            {formError && <div className="p-3 mb-4 rounded text-sm text-red-500 bg-red-500/10">{formError}</div>}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Company</label>
                  <select required value={formData.company_id} onChange={e => setFormData({...formData, company_id: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]">
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Vendor Company Name</label>
                  <input required type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Vendor Type</label>
                  <select value={formData.vendor_type} onChange={e => setFormData({...formData, vendor_type: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]">
                    <option value="Contractor">Contractor</option>
                    <option value="Supplier">Supplier</option>
                    <option value="Consultant">Consultant</option>
                    <option value="other">+ Add New Type...</option>
                  </select>
                  {formData.vendor_type === 'other' && (
                    <input required type="text" placeholder="Custom type" value={formData.custom_type} onChange={e => setFormData({...formData, custom_type: e.target.value})} className="w-full mt-2 p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                  )}
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Details of Goods / Services</label>
                  <input type="text" placeholder="e.g. Cement Supply or Architectural Design" value={formData.goods_services} onChange={e => setFormData({...formData, goods_services: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
              </div>

              <div className="flex gap-3 pt-6">
                <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-2 rounded font-medium bg-[var(--bg-panel-alt)] text-[var(--text-primary)]">Cancel</button>
                <button type="submit" className="flex-1 py-2 rounded font-medium text-white" style={{ backgroundColor: 'var(--brand-accent)' }}>Save Vendor</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
